import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

gsap.registerPlugin(ScrollTrigger);

const DonutFlavors = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  const flavors = [
    {
      name: 'Cinna Dough - Cinnamon Swirl Donut',
      description: 'A cinnamon roll and doughnut fusion with a soft sugar glaze or white icing.',
      image: '/images/Cinnamon-Donut.png',
      color: 'amber',
      popular: true
    },
    {
      name: 'Lemon Cheesecake Donut - Creamy Citrus Crumble',
      description: 'Fluffy doughnut filled with cream cheese, topped with zesty lemon icing and golden biscuit crumbs. A tangy-sweet cheesecake twist. Vegetarian.',
      image: '/images/Lemon-cheesecake-donut.png',
      color: 'pink',
      popular: false
    },
    {
      name: 'Jammy Doh! - Raspberry Jam & White Choc',
      description: 'Fluffy doughnut filled with raspberry jam, coated in white chocolate and topped with a Jammy Dodger®️ biscuit. A vegan delight.',
      image: '/images/jammy-donut.png',
      color: 'teal',
      popular: true
    },
    {
      name: 'Kinderella - Bueno Filled Donut',
      description: 'Fluffy doughnut coated in milk chocolate, filled with white chocolate hazelnut spread, and topped with half a Kinder Bueno® bar.',
      image: '/images/kinderella-donut.png',
      color: 'amber',
      popular: true
    },
    {
      name: 'Lotus Bliss - Caramel & Biscoff Donut',
      description: 'Caramel-iced doughnut filled with speculoos spread, topped with Lotus Biscoff® crumbs and finished with a whole Lotus Biscoff® biscuit. Vegan-friendly.',
      image: '/images/lotus-biscof-donut.png',
      color: 'pink',
      popular: false
    },
    {
      name: 'Kitella - Chocolate Hazelnut & KitKat® Donut',
      description: 'Fluffy milk chocolate-coated doughnut with chocolate hazelnut filling and KitKat® topping.',
      image: '/images/kitella-donut.png',
      color: 'teal',
      popular: true
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(card,
          { y: 80, opacity: 0, scale: 0.8 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.7,
            ease: 'back.out(1.4)',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none none'
            }
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const getColorClasses = (color) => {
    const colors = {
      amber: 'border-amber-300 hover:border-amber-500',
      pink: 'border-pink-300 hover:border-pink-500',
      teal: 'border-teal-300 hover:border-teal-500'
    };
    return colors[color];
  };

  return (
    <section id="flavors" ref={sectionRef} className="py-20 px-4 bg-gradient-to-br from-pink-50 via-white to-amber-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Delicious Flavors
          </h2>
          <p className="text-xl text-gray-600">
            Every donut is freshly made with love and care
          </p>
          <div className="flex justify-center space-x-2 mt-4">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className="inline-block w-2 h-2 rounded-full"
                style={{
                  backgroundColor: ['#ec4899', '#14b8a6', '#fbbf24', '#f97316', '#ec4899'][i]
                }}
              />
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {flavors.map((flavor, index) => (
            <div
              key={index}
              ref={el => cardsRef.current[index] = el}
              className="flavor-card"
            >
              <Card className={`h-full transition-all duration-300 hover:shadow-2xl hover:-translate-y-3 border-4 ${getColorClasses(flavor.color)} relative overflow-hidden group`}>
                {flavor.popular && (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                      Popular
                    </Badge>
                  </div>
                )}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={flavor.image}
                    alt={flavor.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-2xl text-gray-900 mb-3">{flavor.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{flavor.description}</p>
                  
                  {/* Sprinkle decoration */}
                  <div className="flex space-x-1 mt-4 opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                    {[...Array(8)].map((_, i) => (
                      <span
                        key={i}
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{
                          backgroundColor: ['#ec4899', '#14b8a6', '#fbbf24'][i % 3]
                        }}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-gray-700">
            All flavors available in bulk orders. Mix and match to create your perfect selection! More flavours upon request including Vegan options
          </p>
        </div>
      </div>
    </section>
  );
};

export default DonutFlavors;